// m0_sessions.ts — typed API wrappers for openapi.fyd.v1.yaml session endpoints.
//
// Maps each FE wrapper to its canonical operationId from
// docs/contracts/m0/openapi.fyd.v1.yaml (15 endpoints; this file owns 13 of them
// — the other 2 are auth/clerk-webhook and billing/webhook, server-only).
//
// Idempotency: 6 mutating routes set Idempotency-Key per auth_middleware.md §6.
//   sessionCreate / sessionAbort / authWalletSign / paymentUsdtInitiate /
//   materialUpload / materialDelete

import { api, ApiError } from './client';
import type { IntentPreset, IntentVector } from '@/types/intent';
import type { TranscriptResponse } from '@/types/transcript';

// ============================================================================
// M2 Types (FE_SPEC §13 + §6 + §7 + §12)
// ============================================================================

/**
 * 12 phase closed-set name. Mirrors `cockpit/src/ui/phases/PhaseProps.ts
 * PhaseName`. Re-declared here so the API layer doesn't take a runtime dep
 * on the UI layer.
 */
export type M2PhaseName =
  | 'framing'
  | 'exploration'
  | 'narrowing'
  | 'challenging'
  | 'validation'
  | 'synthesis'
  | 'listening'
  | 'reflection'
  | 'decomposition'
  | 'recomposition'
  | 'stress_testing'
  | 'mediation';

/**
 * 10 + 1 (unknown_council) scene_symbols closed-set (PRD §5).
 * Re-declared so API layer is self-contained — UI layer mirrors via re-export.
 */
export type SceneSymbolId =
  | 'decision_high_stakes'
  | 'decision_low_stakes'
  | 'technical_evaluation'
  | 'creative_exploration'
  | 'risk_analysis'
  | 'emotional_processing'
  | 'value_clarification'
  | 'strategy_planning'
  | 'conflict_resolution'
  | 'knowledge_synthesis'
  | 'unknown_council';

export interface SceneSymbol {
  id: SceneSymbolId;
  confidence: number; // [0,1]
}

export type GatingMode = 'auto' | 'user_advance';

export type AttachmentType = 'pdf' | 'markdown' | 'url' | 'text';

/** AttachmentInput — NewSessionDialog payload. multipart 序列化在 dialog 层完成. */
export interface AttachmentInput {
  type: AttachmentType;
  url?: string;       // 文件类: BE persist 后路径; URL 类: 用户输入的 URL
  content?: string;   // 链接/文本类的 inline content
  title: string;
}

/** ChoreographyDTO — POST /sessions 200 返回值的 phase 编排 (FE_SPEC §12.2). */
export interface ChoreographyPhase {
  name: string;       // 12 闭集
  speakers: string[]; // PersonaId — typed as string here to keep API layer UI-agnostic.
  budget_sec: number;
  iron_tone: 'standard' | 'compassionate_critique' | null;
}

export interface ChoreographyDTO {
  choreography_id: string;
  scenes_matched: SceneSymbolId[];
  phases: ChoreographyPhase[];
  tone_profile: {
    politeness: number;
    elegance: number;
    trustworthy: number;
    comforting: number;
  };
  gating_mode: GatingMode;
  rationale: string;
}

/** IntentInferredDTO — POST /sessions 200 返回值的 BE 推断 intent (FE_SPEC §13.2). */
export interface IntentInferredDTO {
  intent_vector: IntentVector;
  scene_symbols: SceneSymbol[];
  rationale: string;
}

/**
 * AttachmentBriefingDTO — sage briefing summary (FE_SPEC §6 / PRD §2.5.9).
 * BE 永远返完整 object (FIX-C4) · `available=false` 时 4 count 字段全 0 / raw_path=""。
 */
export interface AttachmentBriefingDTO {
  available: boolean;
  key_facts_count: number;
  key_data_count: number;
  contradictions_count: number;
  gaps_count: number;
  char_count: number;
  raw_path: string;
  quality_compromised: boolean;
  content?: string | null;
}

/**
 * 12 错误码闭集 (FE_SPEC §13.6 · PRD §7.10.4).
 * UI 路由按 e.code switch · 不再仅依赖 status code。
 */
export type ErrorCode =
  | 'topic_not_applicable'
  | 'attachment_too_large'
  | 'attachment_parse_failed'
  | 'url_unsafe'
  | 'composer_unavailable'
  | 'composer_invariant_violation'
  | 'content_filter_blocked'
  | 'llm_5xx_transient'
  | 'llm_5xx_prolonged'
  | 'storage_pressure'
  | 'session_deadline'
  | 'quality_compromised';

// ============================================================================
// Sessions
// ============================================================================

export interface SessionCreateRequest {
  topic: string;
  // Legacy vocational role (NOT a council persona). a7 P2 fix: do not conflate
  // with PersonaId — that confusion is what the a7 audit caught.
  role?: 'full_stack' | 'backend' | 'frontend' | 'mobile' | 'ai_ml' | 'ops' | 'other';
  stage?: 'ideation' | 'mvp' | 'early_traction' | 'scaling';
  time_budget_h_per_week?: number;
  material_ids?: string[];
  // ★ M0.5 — when omitted, BE auto-classifies the topic into an IntentVector.
  // Caller maps the dropdown 'auto' selection to "omit this field".
  intent_preset?: IntentPreset;
  // ★ M2 (FE_SPEC §13.1) — attachments[] 5 件以内 · BE multipart parse 后写
  // /data/uploads/{session_id}/. Wire form: JSON array of AttachmentInput.
  attachments?: AttachmentInput[];
  // ★ M2 (FE_SPEC §10.5 P0-9 free_retry_eligible): 同 user_id retry 上一议会 · BE
  // 不扣费起新议会 · BE 用 retry_of 链接 quality_compromised + free retry 关联。
  retry_of?: string;
}

export interface SessionCreateResponse {
  session_id: string;
  sse_url: string;
  payment_required: boolean;
  // ★ M0.5 — present iff BE classified or preset matched. Legacy sessions: undefined.
  intent_vector?: IntentVector;
  // ★ M2 (FE_SPEC §13.2) — Composer 返 intent inference + choreography 编排.
  intent_inferred?: IntentInferredDTO;
  choreography?: ChoreographyDTO;
  attachment_briefing?: AttachmentBriefingDTO;
  // ★ M2 (FE_SPEC §9.2 P0-12) — 503 fallback path · BE 用静态 fallback choreography.
  fallback_used?: boolean;
  fallback_reason?: string;
}

/**
 * sessionCreate — POST /api/v1/sessions  (operationId: session_create)
 *
 * Topic: ≥50 chars, ≤2000 (F-02). material_ids ≤5.
 * Auto-attaches Idempotency-Key (UUID v4) — caller may override via opts.idempotencyKey.
 * Errors: topic_too_short / topic_too_long / material_quota_exceeded /
 *         payment_required / clerk_jwt_invalid / wallet_used_free / ip_rate_limited.
 */
export async function sessionCreate(
  body: SessionCreateRequest,
  opts?: { idempotencyKey?: string; signal?: AbortSignal },
): Promise<SessionCreateResponse> {
  const headers: Record<string, string> = {};
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  return api<SessionCreateResponse>('/sessions', {
    method: 'POST',
    body,
    headers,
    idempotent: true,
    signal: opts?.signal,
  });
}

export interface SessionDetail {
  session_id: string;
  status: string;
  user_id?: string;
  current_phase?: string;
  sse_url: string | null;
  abort_reason?: string;
  // ★ M0.5 — present iff BE classified or preset matched. Legacy sessions: undefined.
  intent_vector?: IntentVector;
}

// ★ M0.5 — SessionMeta (history list item) — used by GET /api/v1/sessions and
// rendered by SessionListCard glyph. intent_vector is optional for legacy
// sessions created pre-M0.5.
export interface SessionMeta {
  session_id: string;
  user_id: string;
  question: string;
  status: 'running' | 'completed' | 'aborted';
  created_at: string;
  abort_reason?: string;
  intent_vector?: IntentVector;
}

/** sessionGetDetail — GET /api/v1/sessions/{id} (operationId: session_get_detail) */
export async function sessionGetDetail(
  sessionId: string,
  opts?: { signal?: AbortSignal },
): Promise<SessionDetail> {
  return api<SessionDetail>(`/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'GET',
    signal: opts?.signal,
  });
}

/** sessionAbort — POST /api/v1/sessions/{id}/abort (operationId: session_abort) */
export async function sessionAbort(
  sessionId: string,
  opts?: { idempotencyKey?: string; signal?: AbortSignal },
): Promise<void> {
  const headers: Record<string, string> = {};
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  await api<void>(`/sessions/${encodeURIComponent(sessionId)}/abort`, {
    method: 'POST',
    headers,
    idempotent: true,
    signal: opts?.signal,
  });
}

export interface SessionAdvanceResponse {
  advanced_to_phase: M2PhaseName | string; // 12 闭集 (string fallback for forward-compat)
}

/**
 * sessionAdvance — POST /api/v1/sessions/{id}/advance (★ M2 新增 · FE_SPEC §13.3).
 *
 * ★ FIX-C3 (PRD §11 + §7.10.10 · BE_SPEC §9 真值): body 必带 `phase_idx`
 *   = 当前 paused phase 的 index · 来源 store.current_phase_idx (§12 store
 *   字段 · 由 phase_paused payload `phase_index` 字段写入)。
 *   BE handler 用 phase_idx 校验 epoch · 不匹配 → 409 phase_already_advanced。
 *
 * 错误码 (P0-10 + P0-14 + P0-4):
 *   - 409 `phase_already_advanced`: epoch 错位 · FE toast + 隐 button
 *   - 410 `session_terminated`: 已结束 · FE navigate Report
 *   - 401: 走全局 ProtectedRoute
 */
/**
 * ★ pass-5 fix C14: optional endpointOverride from phase_paused payload.
 * Validates against the canonical `/api/v1/sessions/{ulid}/advance` shape;
 * falls back to derived path on regex miss.
 */
const ADVANCE_OVERRIDE_RE = /^\/api\/v1\/sessions\/s[0-9a-z]{26}\/advance$/;

export async function sessionAdvance(
  sessionId: string,
  phaseIdx: number,
  opts?: { idempotencyKey?: string; signal?: AbortSignal; endpointOverride?: string },
): Promise<SessionAdvanceResponse> {
  const headers: Record<string, string> = {};
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  let path = `/sessions/${encodeURIComponent(sessionId)}/advance`;
  if (opts?.endpointOverride && ADVANCE_OVERRIDE_RE.test(opts.endpointOverride)) {
    // strip /api/v1 prefix because api(...) re-prepends the version base.
    path = opts.endpointOverride.replace(/^\/api\/v1/, '');
  } else if (opts?.endpointOverride) {
    // eslint-disable-next-line no-console
    console.warn('sessionAdvance_endpointOverride_invalid', opts.endpointOverride);
  }

  return api<SessionAdvanceResponse>(path, {
    method: 'POST',
    headers,
    body: { phase_idx: phaseIdx },
    idempotent: true,
    signal: opts?.signal,
  });
}

/**
 * sessionGetTranscript — GET /api/v1/sessions/{id}/transcript
 *   (operationId: session_get_transcript · M0.5 council mechanism increment · Bug B fix)
 *
 * Returns the persisted transcript for a session: ordered list of SSE events
 * + ended flag + head_event_id + optional abort info. FE calls this on
 * /sessions/{id}/live mount BEFORE subscribing to live SSE so reload mid-
 * council repaints persona speech bubbles instead of showing empty cards.
 *
 * Errors:
 *   - 401 clerk_jwt_invalid           (m0 base; ProtectedRoute redirects to /auth)
 *   - 403 session_unauthorized         (other user's session)
 *   - 404 transcript_not_available     (session evicted from registry)
 *
 * Caller convention: optional sinceEventId for delta-only fetch. Aligns with
 * Last-Event-ID semantics on /sse so a single FE retry-loop can use either.
 */
export async function sessionGetTranscript(
  sessionId: string,
  sinceEventId?: number,
  opts?: { signal?: AbortSignal },
): Promise<TranscriptResponse> {
  const path = sinceEventId && sinceEventId > 0
    ? `/sessions/${encodeURIComponent(sessionId)}/transcript?since_event_id=${sinceEventId}`
    : `/sessions/${encodeURIComponent(sessionId)}/transcript`;
  return api<TranscriptResponse>(path, {
    method: 'GET',
    signal: opts?.signal,
  });
}

// ============================================================================
// Reports
// ============================================================================

/** reportGetJson — GET /api/v1/reports/{id} (operationId: report_get_json) */
export async function reportGetJson<T = unknown>(
  reportId: string,
  opts?: { signal?: AbortSignal },
): Promise<T> {
  return api<T>(`/reports/${encodeURIComponent(reportId)}`, {
    method: 'GET',
    signal: opts?.signal,
  });
}

/**
 * reportDownloadMd — GET /api/v1/reports/{id}.md (operationId: report_get_md)
 *
 * Returns markdown body as a Blob with a JSON-error fallback on failure.
 * Uses api({ raw: true }) so JWT injection still happens (was the M-1 D03 P0).
 */
export async function reportDownloadMd(
  reportId: string,
  opts?: { signal?: AbortSignal },
): Promise<Blob> {
  const res = await api<Response>(`/reports/${encodeURIComponent(reportId)}.md`, {
    method: 'GET',
    raw: true,
    signal: opts?.signal,
  });
  return res.blob();
}

// ============================================================================
// Materials (F-22 — backend stubbed in M0; FE wrappers ready for M1+)
// ============================================================================

export interface MaterialUploadRequest {
  filename: string;
  mime_type: string;
  size_bytes: number;
  content_b64: string; // base64-encoded; backend stub will reject in M0
}

export interface MaterialRecord {
  material_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  uploaded_at: string;
  url?: string;
}

/** materialUpload — POST /api/v1/materials (operationId: material_upload) */
export async function materialUpload(
  body: MaterialUploadRequest,
  opts?: { idempotencyKey?: string; signal?: AbortSignal },
): Promise<MaterialRecord> {
  const headers: Record<string, string> = {};
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  return api<MaterialRecord>('/materials', {
    method: 'POST',
    body,
    headers,
    idempotent: true,
    signal: opts?.signal,
  });
}

/** materialDelete — DELETE /api/v1/materials/{id} (operationId: material_delete) */
export async function materialDelete(
  materialId: string,
  opts?: { idempotencyKey?: string; signal?: AbortSignal },
): Promise<void> {
  const headers: Record<string, string> = {};
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  await api<void>(`/materials/${encodeURIComponent(materialId)}`, {
    method: 'DELETE',
    headers,
    idempotent: true,
    signal: opts?.signal,
  });
}

/** materialGetUrl — GET /api/v1/materials/{id} (operationId: material_get_url) */
export async function materialGetUrl(
  materialId: string,
  opts?: { signal?: AbortSignal },
): Promise<MaterialRecord> {
  return api<MaterialRecord>(`/materials/${encodeURIComponent(materialId)}`, {
    method: 'GET',
    signal: opts?.signal,
  });
}

// ============================================================================
// Wallet (F-23 — backend stubbed in M0; FE wrappers ready for paid flow)
// ============================================================================

export interface WalletSignRequest {
  wallet_address: string;
  chain_id: number;
  nonce: string;
  signature: string;
}

export interface WalletSignResponse {
  wallet_id: string;
  free_sample_eligible: boolean;
}

/** authWalletSign — POST /api/v1/auth/wallet/sign (operationId: auth_wallet_sign) */
export async function authWalletSign(
  body: WalletSignRequest,
  opts?: { idempotencyKey?: string; signal?: AbortSignal },
): Promise<WalletSignResponse> {
  const headers: Record<string, string> = {};
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  return api<WalletSignResponse>('/auth/wallet/sign', {
    method: 'POST',
    body,
    headers,
    idempotent: true,
    signal: opts?.signal,
  });
}

export interface WalletFreeSampleStatus {
  eligible: boolean;
  reason?: string;
  ip_rate_limit_remaining: number;
}

/**
 * authWalletFreeSampleStatus —
 * GET /api/v1/auth/wallet/free-sample-status (operationId: auth_wallet_free_sample_status)
 */
export async function authWalletFreeSampleStatus(
  opts?: { signal?: AbortSignal },
): Promise<WalletFreeSampleStatus> {
  return api<WalletFreeSampleStatus>('/auth/wallet/free-sample-status', {
    method: 'GET',
    signal: opts?.signal,
  });
}

// ============================================================================
// Payments (F-08 — backend stubbed in M0; FE wrappers ready for paid flow)
// ============================================================================

export interface PaymentInitiateRequest {
  session_id: string;
  amount_usd: number;
  network: 'TRC20' | 'ERC20';
}

export interface PaymentInitiateResponse {
  payment_id: string;
  qr_uri: string;
  deposit_address: string;
  expires_at: string;
}

/** paymentUsdtInitiate — POST /api/v1/payments/usdt/initiate (operationId: payment_usdt_initiate) */
export async function paymentUsdtInitiate(
  body: PaymentInitiateRequest,
  opts?: { idempotencyKey?: string; signal?: AbortSignal },
): Promise<PaymentInitiateResponse> {
  const headers: Record<string, string> = {};
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  return api<PaymentInitiateResponse>('/payments/usdt/initiate', {
    method: 'POST',
    body,
    headers,
    idempotent: true,
    signal: opts?.signal,
  });
}

export interface PaymentStatus {
  payment_id: string;
  status: 'pending' | 'confirmed' | 'failed' | 'expired';
  tx_hash?: string;
  amount_received?: number;
  failure_code?: string;
}

/** paymentGetStatus — GET /api/v1/payments/{id}/status (operationId: payment_get_status) */
export async function paymentGetStatus(
  paymentId: string,
  opts?: { signal?: AbortSignal },
): Promise<PaymentStatus> {
  return api<PaymentStatus>(`/payments/${encodeURIComponent(paymentId)}/status`, {
    method: 'GET',
    signal: opts?.signal,
  });
}

// ============================================================================
// SSE — token-bound EventSource
// ============================================================================

/**
 * openSessionStream — open EventSource at sse_url returned by sessionCreate.
 *
 * Caller MUST pass the full sse_url (including ?token=...). The bare-id form
 * is gone (was guaranteed-401 per M-1 R1 D04). EventSource cannot set
 * Authorization headers, so the stream_token query param is the auth.
 */
export function openSessionStream(sseUrl: string): EventSource {
  if (!sseUrl.includes('token=')) {
    throw new Error(
      'openSessionStream: sse_url is missing ?token=. Use sessionCreate.sse_url verbatim.',
    );
  }
  return new EventSource(sseUrl, { withCredentials: false });
}

export { ApiError };
